// Returns critical data for building a custom Selector for your product attribute.

import { useCustomizationContext } from '@apps/card/routes/CardCustomizer';
import type { ProductAttributeState, useProductAttributeStates } from '../../utils/state/useProductAttributeStates';
import { getProductAttribute } from '../../utils/getProductAttribute';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import { useMemo } from 'react';
import type { Option } from '../../product.types';
import { useProductAttribute } from '../../useProductAttributes';

type useGetAttributeDataForSelectorProps = {
  attributeId: string;
  getAttribute: ReturnType<typeof useProductAttributeStates>['getAttribute'];
  product: StationeryProduct;
};

const optionIsNotNull = (input: unknown): input is Option => {
  return !!input;
};

export const useGetAttributeDataForSelector = (props: useGetAttributeDataForSelectorProps) => {
  const { attributeId, getAttribute, product } = props;
  const [draftData] = useCustomizationContext(draft => draft.config);

  // Attribute Info
  const attributeUIConfig = useProductAttribute(attributeId);
  const attributeState = getAttribute(attributeId);
  const productAttributeConfig = getProductAttribute(props);

  const otherAttributeStates = useMemo(() => {
    const otherAttributes = product.attributes.filter(attr => attr.attributeId !== attributeId);
    return otherAttributes
      .map(attr => {
        return {
          id: attr.attributeId,
          state: getAttribute(attr.attributeId),
        };
      })
      .filter(attr => attr.state !== undefined) as { id: string; state: ProductAttributeState }[];
  }, [attributeId, getAttribute, product.attributes]);

  const productAttribute = product.attributes.find(attr => attr.attributeId === attributeId);
  if (!attributeUIConfig || !productAttributeConfig || !attributeState || !productAttribute) {
    return null;
  }

  const productOptions = productAttribute.attributeOptions.map(option => attributeUIConfig.options.find(opt => opt.optionId === option.attributeOption.id)).filter(optionIsNotNull);
  const selectedOptionId = attributeState?.getOptionId(draftData);
  const selectedOption = productOptions.find(opt => opt?.optionId === selectedOptionId);

  // States for all attributes

  return {
    attribute: {
      productAttribute: productAttributeConfig,
      attributeUIConfig,
    },
    otherAttributeStates,
    options: {
      selected: selectedOption,
      uiConfigurations: productOptions,
    },
    state: attributeState,
    draftData,
  };
};
